import { type Hit, searchClient } from "@algolia/client-search";
import {
  type Accessor,
  batch,
  createEffect,
  createSignal,
  For,
  Show,
  untrack,
} from "solid-js";

import rootStyles from "~/components/Root.module.scss";
import styles from "~/components/SearchResults.module.scss";
import SmartA from "~/components/SmartA";
import { fragmentToJsx } from "~/utils/fragmentToJsx";
import { renderFragmentFromString } from "~/utils/renderFragmentFromString";

const client = searchClient(
  import.meta.env.VITE_ALGOLIA_APP_ID,
  import.meta.env.VITE_ALGOLIA_API_KEY,
);

const SEARCH_HITS_PER_PAGE = 10;

interface SearchResultsProps {
  readonly query: string;
  readonly searchFieldRef: Accessor<HTMLInputElement | undefined>;
  readonly onNavigate: (path: string) => void;
}

export default function SearchResults(props: SearchResultsProps) {
  const [searchPage, setSearchPage] = createSignal(0);
  const [isSearchDragging, setIsSearchDragging] = createSignal(false);
  const [allPagesLoaded, setAllPagesLoaded] = createSignal(true);
  const [searchHits, setSearchHits] = createSignal<Hit<any>[]>([]);
  const [searchHitsNumber, setSearchHitsNumber] = createSignal(0);
  const [searchTime, setSearchTime] = createSignal(0);

  const onPointerDown = () => {
    setIsSearchDragging(true);
  };

  const onPointerUp = () => {
    setIsSearchDragging(false);
  };

  const onPointerMove = () => {
    if (isSearchDragging()) {
      props.searchFieldRef()?.blur();
    }
  };

  const performSearch = async (query: string, append = false) => {
    if (!query) {
      return;
    }

    setSearchPage((page) => (append ? page + 1 : 0));

    const results = await client.searchSingleIndex<any>({
      indexName: import.meta.env.VITE_ALGOLIA_APP_POSTS_INDEX,
      searchParams: {
        query,
        page: untrack(searchPage),
        ignorePlurals: true,
        advancedSyntax: true,
        hitsPerPage: SEARCH_HITS_PER_PAGE,
        highlightPreTag: "<mark>",
        highlightPostTag: "</mark>",
        snippetEllipsisText: "…",
        attributesToRetrieve: ["title", "body", "path", "cover", "category"],
        attributesToHighlight: ["title", "body"],
        attributesToSnippet: ["body:15"],
      },
    });

    if (results.query !== props.query) {
      return;
    }

    batch(() => {
      setSearchHitsNumber(results.nbHits ?? 0);
      setSearchTime(results.processingTimeMS);
      setAllPagesLoaded(
        (results.nbHits ?? 0) <= (searchPage() + 1) * SEARCH_HITS_PER_PAGE,
      );

      if (!append) {
        setSearchHits(results.hits);
        return;
      }

      setSearchHits((old) => [...old, ...results.hits]);
    });

    // const indexToFocus = untrack(searchPage) * SEARCH_HITS_PER_PAGE;

    requestAnimationFrame(() => {
      // const resultsElement = resultsRef.current;
      // if (resultsElement) {
      //   [...resultsElement.querySelectorAll('a')][indexToFocus].focus();
      // }
    });
  };

  createEffect(() => {
    void performSearch(props.query);
  });

  const loadMoreResults = () => {
    void performSearch(props.query, true);
  };

  const searchMeta = () => {
    return `${searchHitsNumber().toLocaleString(import.meta.env.VITE_LOCALE)} ${searchHitsNumber() >= 2 ? "resultados" : "resultado"}, ${searchTime().toLocaleString(
      import.meta.env.VITE_LOCALE,
      {
        style: "unit",
        unit: "millisecond",
      },
    )}`;
  };

  return (
    <div
      class={styles["search-results"]}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <Show when={props.query}>
        <p class={rootStyles.container}>
          {searchMeta()}
          <SmartA href="https://algolia.com">
            <img
              src="/assets/brands/algolia/dark.svg"
              alt="Search by Algolia"
            />
          </SmartA>
        </p>
        <ul class={rootStyles.container}>
          <For each={searchHits()}>
            {(hit) => (
              <li>
                <SmartA
                  href={hit.path}
                  onClick={() => {
                    props.onNavigate(hit.path);
                  }}
                >
                  <div class={styles.image}>
                    <img src={hit.cover} alt="" />
                  </div>
                  <span>{hit.category ?? ""}</span>
                  <h2>
                    {fragmentToJsx(
                      renderFragmentFromString(
                        hit._highlightResult?.title.value,
                      ),
                    )}
                  </h2>
                  <p>
                    {fragmentToJsx(
                      renderFragmentFromString(hit._snippetResult?.body.value),
                    )}
                  </p>
                </SmartA>
              </li>
            )}
          </For>
        </ul>
        <Show when={!allPagesLoaded()}>
          <button
            class={rootStyles.container}
            type="button"
            onClick={loadMoreResults}
            // onKeyDown={focusLastSearchResult}
          >
            Carregar mais...
          </button>
        </Show>
      </Show>
    </div>
  );
}
